import { useParams, Link, Navigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, ShieldCheck, Clock, Truck } from "lucide-react";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import Breadcrumb from "../Breadcrumb";
import FAQSection from "../FAQSection";
import { newServices, getNewServiceBySlug } from "../../data/newServices";
import { business } from "../../config/business";

const ServicePage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const service = slug ? getNewServiceBySlug(slug) : newServices.find((s) => s.path === location.pathname);

  if (!service) return <Navigate to="/" replace />;

  const related = newServices.filter((s) => s.slug !== service.slug).slice(0, 3);

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: service.name,
    provider: { "@type": "Organization", name: business.name },
    description: service.intro,
  };

  return (
    <Layout>
      <SEOHead
        title={`${service.name} | Vasavi Structures`}
        description={`${service.tagline} PEB design, fabrication and erection by Vasavi Structures.`}
        keywords={`${service.name}, ${service.name} Hyderabad, ${service.name} Telangana, PEB manufacturer Hyderabad`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
      </Helmet>
      <Breadcrumb items={[{ name: service.name }]} />

      {/* 1. Hero */}
      <section className="section-padding pb-10">
        <div className="container-custom">
          <h1 className="font-montserrat text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 max-w-3xl">
            {service.name}
          </h1>
          <p className="text-lg text-secondary font-montserrat font-semibold mb-4">{service.tagline}</p>
          <p className="text-muted-foreground max-w-3xl text-lg mb-8">{service.intro}</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/enquiry" className="btn-primary inline-block px-8 py-4 text-center">
              Discuss Your Project
            </Link>
            <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="btn-outline inline-block px-8 py-4 text-center">
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* 2. Trust strip */}
      <section className="bg-primary py-6">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-primary-foreground text-center">
            <span className="font-montserrat font-semibold flex items-center gap-2"><ShieldCheck size={18} aria-hidden="true" /> Site-specific structural design</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Clock size={18} aria-hidden="true" /> Parallel fabrication & foundation work</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Truck size={18} aria-hidden="true" /> Telangana & AP delivery</span>
          </div>
        </div>
      </section>

      {/* 3. Applications */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
            Applications
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {service.applications.map((app) => (
              <div key={app} className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border">
                <CheckCircle className="text-secondary flex-shrink-0" size={20} aria-hidden="true" />
                <span className="text-foreground">{app}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Benefits */}
      <section className="section-padding bg-muted">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-foreground mb-10 text-center">
            Why This Approach
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.benefits.map((b) => (
              <div key={b.title} className="p-6 bg-card rounded-xl shadow-md text-center card-hover">
                <h3 className="font-montserrat font-bold text-foreground mb-2">{b.title}</h3>
                <p className="text-sm text-muted-foreground">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Process */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-foreground mb-10 text-center">
            Our Process
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.process.map((step, i) => (
              <div key={step.title} className="p-6 bg-card rounded-xl border border-border">
                <p className="text-xs font-montserrat font-bold text-secondary mb-2">STEP {i + 1}</p>
                <h3 className="font-montserrat font-bold text-foreground mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Related services */}
      <section className="section-padding bg-muted">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl font-bold text-foreground mb-6 text-center">
            Related Services
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {related.map((s) => (
              <Link key={s.slug} to={s.path} className="p-5 bg-card rounded-xl shadow-md card-hover border border-border">
                <h3 className="font-montserrat font-bold text-foreground mb-1">{s.name}</h3>
                <p className="text-sm text-muted-foreground">{s.tagline}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 7. FAQ */}
      <FAQSection faqs={service.faqs} title={`${service.name} — Frequently Asked Questions`} />

      {/* 8. CTA */}
      <section className="section-padding bg-secondary">
        <div className="container-custom text-center">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-secondary-foreground mb-4">
            Ready to Discuss Your {service.name} Project?
          </h2>
          <p className="text-secondary-foreground/90 mb-8 max-w-xl mx-auto">
            Share your requirements and our engineering team will get back with a design consultation.
          </p>
          <Link to="/enquiry" className="btn-primary inline-block px-8 py-4">
            Get Project Estimate
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default ServicePage;
